<template>
    <div v-show="stepActive" class="tournament-basic-info">
        <h2 class="title">{{ $t('firstSteps') }}</h2>
        <p class="subtitle">{{ $t('addBasicInfo') }}</p>

        <div class="info-card">
            <v-form>
                <!-- Community Selector - Only visible in broker app -->
                <v-select
                    v-if="isBrokerApp"
                    v-model="selectedClubId"
                    :items="ownedClubs"
                    :label="$t('selectCommunity')"
                    :hint="$t('selectCommunityHint')"
                    :loading="loadingClubs"
                    :error-messages="clubError"
                    item-text="name"
                    item-value="id"
                    outlined
                    dense
                    persistent-hint
                    class="mb-4"
                    @change="validateForm"
                >
                    <template v-slot:selection="{ item }">
                        <div class="community-selection">
                            <v-avatar size="24" class="mr-2">
                                <v-img v-if="item.imageUrl" :src="item.imageUrl" />
                                <v-icon v-else small>fa-regular fa-users</v-icon>
                            </v-avatar>
                            <span>{{ item.name }}</span>
                        </div>
                    </template>
                    <template v-slot:item="{ item }">
                        <v-list-item-avatar size="32">
                            <v-img v-if="item.imageUrl" :src="item.imageUrl" />
                            <v-icon v-else>fa-regular fa-users</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                            <v-list-item-title>{{ item.name }}</v-list-item-title>
                        </v-list-item-content>
                    </template>
                </v-select>

                <v-text-field
                    v-model="tournamentName"
                    :label="$t('tournamentName')"
                    :counter="maxNameLength"
                    :error-messages="nameError"
                    outlined
                    dense
                    @input="validateForm"
                ></v-text-field>

                <v-select
                    v-model="tournamentLanguage"
                    :items="availableLanguages"
                    :label="$t('tournamentLanguage')"
                    :hint="$t('tournamentLanguageHint')"
                    item-text="name"
                    item-value="code"
                    outlined
                    dense
                    persistent-hint
                    @change="validateForm"
                >
                    <template v-slot:selection="{ item }">
                        <div class="language-selection">
                            <Flag :code="item.flag" :squared="false" class="flag-small" />
                            <span class="language-name-small">{{ item.name }}</span>
                        </div>
                    </template>
                    <template v-slot:item="{ item }">
                        <v-list-item-avatar size="24">
                            <Flag :code="item.flag" :squared="false" />
                        </v-list-item-avatar>
                        <v-list-item-title>{{ item.name }}</v-list-item-title>
                    </template>
                </v-select>

                <div class="dates-container">
                    <div class="date-time-group">
                        <v-menu
                            ref="startMenu"
                            v-model="startMenu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="startDate"
                                    :label="$t('startDate')"
                                    :error-messages="startDateError"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                    outlined
                                    dense
                                    class="date-field"
                                    @input="validateForm"
                                >
                                    <template v-slot:append>
                                        <i class="fa-regular fa-calendar"></i>
                                    </template>
                                </v-text-field>
                            </template>
                            <v-date-picker
                                v-model="startDate"
                                no-title
                                :min="minStartDate"
                                @input="handleStartDateSelected"
                            ></v-date-picker>
                        </v-menu>

                        <v-tooltip 
                            bottom 
                            max-width="300"
                            color="var(--v-mainGreen-base)"
                            content-class="utc-tooltip-content"
                            nudge-bottom="-10"
                        >
                            <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                                <v-text-field
                                    v-bind="tooltipAttrs"
                                    v-on="tooltipOn"
                                    v-model="startTime"
                                    :label="$t('startTime')"
                                    :placeholder="'09:00'"
                                    :hint="$t('timeFormat24hr')"
                                    :error-messages="startTimeError"
                                    outlined
                                    dense
                                    class="time-field"
                                    maxlength="5"
                                    @input="validateForm"
                                    @blur="formatTime('start')"
                                >
                                    <template v-slot:append>
                                        <v-icon small color="mainGreen">fa-info-circle</v-icon>
                                    </template>
                                </v-text-field>
                            </template>
                            <span class="white--text">{{ $t('utcTooltip') }}</span>
                        </v-tooltip>
                    </div>

                    <div class="date-time-group">
                        <v-menu
                            ref="endMenu"
                            v-model="endMenu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="endDate"
                                    :label="$t('endDate')"
                                    :error-messages="endDateError"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                    outlined
                                    dense
                                    class="date-field"
                                    @input="validateForm"
                                >
                                    <template v-slot:append>
                                        <i class="fa-regular fa-calendar"></i>
                                    </template>
                                </v-text-field>
                            </template>
                            <v-date-picker
                                v-model="endDate"
                                no-title
                                :min="minEndDate"
                                @input="handleEndDateSelected"
                            ></v-date-picker>
                        </v-menu>

                        <v-tooltip 
                            bottom 
                            max-width="300"
                            color="var(--v-mainGreen-base)"
                            content-class="utc-tooltip-content"
                            nudge-bottom="-10"
                        >
                            <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                                <v-text-field
                                    v-bind="tooltipAttrs"
                                    v-on="tooltipOn"
                                    v-model="endTime"
                                    :label="$t('endTime')"
                                    :placeholder="'18:00'"
                                    :hint="$t('timeFormat24hr')"
                                    :error-messages="endTimeError"
                                    outlined
                                    dense
                                    class="time-field"
                                    maxlength="5"
                                    @input="validateForm"
                                    @blur="formatTime('end')"
                                >
                                    <template v-slot:append>
                                        <v-icon small color="mainGreen">fa-info-circle</v-icon>
                                    </template>
                                </v-text-field>
                            </template>
                            <span class="white--text">{{ $t('utcTooltip') }}</span>
                        </v-tooltip>
                    </div>
                </div>

                <v-text-field
                    v-model="faqsLink"
                    :label="$t('faqs')"
                    :error-messages="faqsError"
                    outlined
                    dense
                    @input="validateForm"
                >
                    <template v-slot:append>
                        <i class="fa-regular fa-link"></i>
                    </template>
                </v-text-field>
            </v-form>
        </div>
    </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

export default {
    name: 'TournamentBasicInfo',
    props: {
        stepActive: {
            type: Boolean,
            required: true
        },
        setNextBtnDisabled: {
            type: Function,
            required: true
        }
    },
    data: () => ({
        startMenu: false,
        endMenu: false,
        startDateError: '',
        endDateError: '',
        startTimeError: '',
        endTimeError: '',
        nameError: '',
        faqsError: '',
        clubError: '',
        minStartDate: new Date().toISOString().substr(0, 10),
        maxNameLength: 28,
        availableLanguages: [
            { code: 'en', name: 'English', flag: 'US' },
            { code: 'es', name: 'Español', flag: 'ES' },
            { code: 'ko', name: '한국어', flag: 'KR' },
            { code: 'th', name: 'ไทย', flag: 'TH' },
            { code: 'pt', name: 'Português', flag: 'PT' },
            { code: 'zh', name: '简体中文', flag: 'CN' },
            { code: 'zh-Hant', name: '繁體中文', flag: 'CN' },
            { code: 'vi', name: 'Tiếng Việt', flag: 'VN' }
        ],
        // Broker app community selection
        ownedClubs: [],
        loadingClubs: false
    }),
    computed: {
        ...mapState({
            formData: state => state.tournamentForm.basicInfo
        }),
        // Check if running in broker app mode
        isBrokerApp() {
            return process.env.VUE_APP_BROKER_STANDALONE === 'true';
        },
        // Selected club ID for broker app
        selectedClubId: {
            get() {
                return this.formData.selectedClubId;
            },
            set(value) {
                this.updateTournamentBasicInfo({
                    ...this.formData,
                    selectedClubId: value
                });
            }
        },
        tournamentName: {
            get() {
                return this.formData.name;
            },
            set(value) {
                this.updateTournamentBasicInfo({
                    ...this.formData,
                    name: value
                });
            }
        },
        startDate: {
            get() {
                return this.formData.startDate;
            },
            set(value) {
                this.updateTournamentBasicInfo({
                    ...this.formData,
                    startDate: value
                });
            }
        },
        startTime: {
            get() {
                return this.formData.startTime || '09:00';
            },
            set(value) {
                this.updateTournamentBasicInfo({
                    ...this.formData,
                    startTime: value
                });
            }
        },
        endDate: {
            get() {
                return this.formData.endDate;
            },
            set(value) {
                this.updateTournamentBasicInfo({
                    ...this.formData,
                    endDate: value
                });
            }
        },
        endTime: {
            get() {
                return this.formData.endTime || '18:00';
            },
            set(value) {
                this.updateTournamentBasicInfo({
                    ...this.formData,
                    endTime: value
                });
            }
        },
        faqsLink: {
            get() {
                return this.formData.faqsLink;
            },
            set(value) {
                this.updateTournamentBasicInfo({
                    ...this.formData,
                    faqsLink: value
                });
            }
        },
        tournamentLanguage: {
            get() {
                return this.formData.mainLanguage || this.$i18n.locale;
            },
            set(value) {
                this.updateTournamentBasicInfo({
                    ...this.formData,
                    mainLanguage: value
                });
            }
        },
        minEndDate() {
            return this.startDate || this.minStartDate;
        }
    },
    async mounted() {
        // Fetch owned clubs if in broker app mode
        if (this.isBrokerApp) {
            await this.fetchOwnedClubs();
        }
    },
    methods: {
        ...mapMutations(['updateTournamentBasicInfo']),

        /**
         * Fetch owned clubs for broker admin to select from
         * Only called in broker app mode
         */
        async fetchOwnedClubs() {
            this.loadingClubs = true;
            this.clubError = '';

            try {
                // Dynamic import to avoid loading broker repository in traders app
                const clubsRepository = await import('@/apps/broker/repositories/clubs.repository').then(m => m.default);
                const partnerId = this.$store.getters['broker/partnerId'];

                if (partnerId) {
                    this.ownedClubs = await clubsRepository.getOwnedClubs(partnerId);

                    // Auto-select if only one club is available
                    if (this.ownedClubs.length === 1 && !this.selectedClubId) {
                        this.selectedClubId = this.ownedClubs[0].id;
                    }
                }
            } catch (error) {
                console.error('Error fetching owned clubs:', error);
                this.clubError = this.$t('errorLoadingCommunities');
            } finally {
                this.loadingClubs = false;
                this.validateForm();
            }
        },

        validateForm() {
            this.startDateError = '';
            this.endDateError = '';
            this.startTimeError = '';
            this.endTimeError = '';
            this.nameError = '';
            this.faqsError = '';
            this.clubError = '';

            // Validate club selection for broker app
            if (this.isBrokerApp && !this.selectedClubId && this.ownedClubs.length > 0) {
                this.clubError = this.$t('selectCommunityRequired');
            }

            // Validate name length
            if (this.tournamentName.length > this.maxNameLength) {
                this.nameError = this.$t('maxLengthError');
            }

            // Validate time format
            if (this.startTime && !this.isValidTimeFormat(this.startTime)) {
                this.startTimeError = this.$t('invalidTimeFormat');
            }

            if (this.endTime && !this.isValidTimeFormat(this.endTime)) {
                this.endTimeError = this.$t('invalidTimeFormat');
            }

            // Validate FAQs URL if provided
            if (this.faqsLink && !this.isValidUrl(this.faqsLink)) {
                this.faqsError = this.$t('invalidUrlError');
            }

            // For broker app, club selection is required
            const clubValid = !this.isBrokerApp || (this.ownedClubs.length === 0) || this.selectedClubId;

            const isValid = clubValid &&
                           this.tournamentName &&
                           this.tournamentName.length <= this.maxNameLength &&
                           this.startDate &&
                           this.endDate &&
                           this.startTime &&
                           this.endTime &&
                           this.tournamentLanguage &&
                           this.isValidTimeFormat(this.startTime) &&
                           this.isValidTimeFormat(this.endTime) &&
                           this.validateDateTime() &&
                           (!this.faqsLink || this.isValidUrl(this.faqsLink));

            this.setNextBtnDisabled(!isValid);
        },
        validateDateTime() {
            if (!this.startDate || !this.endDate || !this.startTime || !this.endTime) return false;

            const startDateTime = new Date(`${this.startDate}T${this.startTime}`);
            const endDateTime = new Date(`${this.endDate}T${this.endTime}`);
            
            if (endDateTime <= startDateTime) {
                this.endDateError = this.$t('endDateTimeError');
                return false;
            }
            
            return true;
        },
        isValidTimeFormat(time) {
            if (!time) return false;
            const timePattern = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
            return timePattern.test(time);
        },
        formatTime(timeType) {
            let time = timeType === 'start' ? this.startTime : this.endTime;
            
            if (!time) return;
            
            // Remove any non-digit and non-colon characters
            time = time.replace(/[^\d:]/g, '');
            
            // Handle different input formats
            if (time.length === 1 || time.length === 2) {
                // If just hour(s), add :00
                let hour = parseInt(time);
                if (hour >= 0 && hour <= 23) {
                    time = hour.toString().padStart(2, '0') + ':00';
                }
            } else if (time.length === 3 && time.includes(':')) {
                // Format like "9:0" -> "09:00"
                let [hour, minute] = time.split(':');
                hour = parseInt(hour);
                minute = parseInt(minute || 0);
                if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
                    time = hour.toString().padStart(2, '0') + ':' + minute.toString().padStart(2, '0');
                }
            } else if (time.length === 4 && !time.includes(':')) {
                // Format like "0900" -> "09:00"
                let hour = parseInt(time.substr(0, 2));
                let minute = parseInt(time.substr(2, 2));
                if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
                    time = hour.toString().padStart(2, '0') + ':' + minute.toString().padStart(2, '0');
                }
            }
            
            // Update the time value
            if (timeType === 'start') {
                this.startTime = time;
            } else {
                this.endTime = time;
            }
        },
        isValidUrl(url) {
            const urlPattern = /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/;
            return url.trim() === '' || urlPattern.test(url.trim());
        },
        handleStartDateSelected(date) {
            this.startDate = date;
            this.startMenu = false;
            
            // Reset end date if it's before new start date
            if (this.endDate) {
                const end = new Date(this.endDate);
                const start = new Date(date);
                if (end <= start) {
                    this.endDate = '';
                }
            }
            
            this.validateForm();
        },
        handleEndDateSelected(date) {
            this.endDate = date;
            this.endMenu = false;
            this.validateForm();
        },
    }
}
</script>



<style lang="scss">
.tournament-basic-info {
    width: 100%;
    
    .title {
        font-size: 32px !important;
        font-weight: bold;
        margin-bottom: 8px;
        font-family: "Avenir", sans-serif;
        color: var(--v-textPrimary-base);
    }

    .subtitle {
        font-size: 16px;
        color: var(--v-textSecondary-base);
        margin-bottom: 24px;
        font-family: "Avenir", sans-serif;
    }
    
    .info-card {
        background-color: var(--v-cardBackground-base);
        border: 1px solid #9A9A9A;
        border-radius: 10px;
        padding: 76px 60px 50px 60px;

        .v-form{
            max-width: 720px;
            margin: auto;
        }

        // Community selector styling
        .community-selection {
            display: flex;
            align-items: center;
        }

        .dates-container {
            display: flex;
            gap: 24px;
            margin-bottom: 16px;

            @media (max-width: 768px) {
                flex-direction: column;
                gap: 16px;
            }

            .date-time-group {
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 12px;

                @media (min-width: 769px) {
                    .date-field {
                        margin-bottom: 8px;
                    }
                }

                @media (max-width: 768px) {
                    .date-field, .time-field {
                        width: 100%;
                    }
                }
            }

            .date-field, .time-field {
                width: 100%;
            }
        }
    }

    //selector for inner icons
    .v-text-field--enclosed.v-input--dense:not(.v-text-field--solo).v-text-field--outlined .v-input__append-inner {
        margin-top: 12px;
        i {
            color: #9a9a9a;
        }
    }

    // Language selection styling
    .language-selection {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .flag-small {
        width: 20px;
        height: 15px;
    }

    .language-name-small {
        font-size: 14px;
    }

}

/* UTC Tooltip Styling */
::v-deep .utc-tooltip-content {
  background-color: var(--v-mainGreen-base) !important;
  color: white !important;
  padding: 8px 12px !important;
  font-size: 13px !important;
  line-height: 1.4 !important;
  border-radius: 4px !important;
  opacity: 0.95 !important;
}

::v-deep .v-tooltip__content {
  pointer-events: auto !important;
}

/* Ensure white text in the tooltip */
::v-deep .utc-tooltip-content .white--text {
  color: white !important;
}
</style>
